import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { listAssetListImports, readAssetListImport } from '../../lib/assetListImportHistoryStore.js'
import { importMembershipDiff, originalCsvBytes } from '../../lib/assetListImportHistory.js'

const outcomeLabel = value => ({ started: 'Unconfirmed / interrupted', completed: 'Completed', partial: 'Partial', failed: 'Failed', stopped: 'Stopped' })[value] || 'Unknown'
const timeLabel = value => value ? new Date(value).toISOString().replace('T', ' ').replace('.000Z', ' UTC').replace('Z', ' UTC') : 'Unknown'
const symbols = rows => rows.map(row => row.symbol).join(', ') || 'None'

// Intentionally plain recorded data. Do not mount AssetListTable, a chart,
// instrument link, live list loader, or any quote/subscription consumer here.
export default function AssetListImportHistoryModal({ list, onClose }) {
  const [entries, setEntries] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [record, setRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState(null)
  const [detailError, setDetailError] = useState(null)
  const [more, setMore] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [retry, setRetry] = useState(0)
  const dialog = useRef(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    const previous = document.activeElement
    dialog.current?.focus()
    return () => { alive.current = false; previous?.focus?.() }
  }, [])
  useEffect(() => {
    let current = true
    setLoading(true); setError(null); setRecord(null); setSelectedId(''); setEntries([])
    listAssetListImports(list.listId).then(rows => {
      if (!current) return
      setEntries(rows); setSelectedId(rows[0]?.import_id || ''); setMore(rows.length === 50)
    }).catch(failure => { if (current) setError(failure.message) })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [list.listId, retry])
  useEffect(() => {
    let current = true
    setRecord(null); setDetailError(null)
    if (!selectedId) { setDetailLoading(false); return }
    setDetailLoading(true)
    readAssetListImport(selectedId).then(data => { if (current) setRecord(data) })
      .catch(failure => { if (current) setDetailError(failure.message) })
      .finally(() => { if (current) setDetailLoading(false) })
    return () => { current = false }
  }, [selectedId])
  useEffect(() => {
    setDownloadUrl(null)
    if (!record) return
    const url = URL.createObjectURL(new Blob([originalCsvBytes(record.original_base64)], { type: 'text/csv' }))
    setDownloadUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [record])

  async function loadOlder() {
    setLoading(true); setError(null)
    try {
      const rows = await listAssetListImports(list.listId, entries.length)
      if (alive.current) { setEntries(current => [...current, ...rows]); setMore(rows.length === 50) }
    } catch (failure) { if (alive.current) setError(failure.message) }
    finally { if (alive.current) setLoading(false) }
  }
  function keys(event) {
    if (event.key === 'Escape') { event.stopPropagation(); onClose() }
    if (event.key !== 'Tab') return
    const nodes = [...dialog.current.querySelectorAll('button, select, a[href], summary')].filter(node => !node.disabled)
    const first = nodes[0], last = nodes.at(-1)
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault(); last?.focus()
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
      event.preventDefault(); first?.focus()
    }
  }
  const diff = record && importMembershipDiff(record.before_members, record.after_members)
  return createPortal(<div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={event => event.stopPropagation()}>
    <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="import-history-title" onKeyDown={keys}
      className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-canvas p-5 text-ink shadow-xl outline-none">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="import-history-title" className="text-lg font-semibold">Import history · {list.name}</h2>
        <button type="button" aria-label="Close import history" onClick={onClose} className="rounded p-1 text-inkMuted hover:text-ink"><X size={20} /></button>
      </div>
      <p className="mb-3 text-sm text-inkMuted">Saved import snapshots only. Manual edits between imports are not tracked. Viewing history does not subscribe to market data.</p>
      <div className="min-h-0 overflow-auto text-sm">
        {error && <div role="alert">{error} <button type="button" onClick={() => setRetry(value => value + 1)} className="underline">Retry history</button></div>}
        {loading && <p role="status">Loading import history…</p>}
        {!loading && !error && !entries.length && <p>No archived imports yet. Earlier uploads were not retained.</p>}
        {entries.length > 0 && <label>Import
          <select aria-label="Historical import" value={selectedId} onChange={event => setSelectedId(event.target.value)} className="mt-1 w-full rounded border border-border bg-canvas2 p-2 text-sm">
            {entries.map(entry => <option key={entry.import_id} value={entry.import_id}>{entry.file_name} · report {entry.report_date || 'unknown'} · uploaded {timeLabel(entry.uploaded_at)} · {outcomeLabel(entry.outcome)}</option>)}
          </select>
        </label>}
        {more && <button type="button" disabled={loading} onClick={() => void loadOlder()} className="mt-2 underline">Load older imports</button>}
        {detailLoading && <p role="status" className="mt-3">Loading saved import…</p>}
        {detailError && <p role="alert" className="mt-3">{detailError} <button type="button" onClick={() => setRetry(value => value + 1)} className="underline">Retry history</button></p>}
        {record && <div className="mt-4 space-y-3">
          <p className="font-semibold">{outcomeLabel(record.outcome)} · {record.import_mode} · {record.list_name}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            <dt>Report / as-of date</dt><dd>{record.report_date || 'Unknown'}</dd>
            <dt>Uploaded to archive</dt><dd>{timeLabel(record.uploaded_at)}</dd>
            <dt>Application started</dt><dd>{timeLabel(record.application_started_at)}</dd>
            <dt>Final snapshot observed</dt><dd>{timeLabel(record.snapshot_at)}</dd>
            <dt>Application completed</dt><dd>{record.completed_at ? timeLabel(record.completed_at) : 'Not confirmed'}</dd>
          </dl>
          <p className="text-xs text-inkMuted">Earlier availability is unknown. The report date does not establish when its contents were known or used. Application is staged; these timestamps do not identify each member's change time.</p>
          {record.outcome === 'started' && <p className="text-ember">Completion was not recorded. Changes may have been applied; final membership is unknown.</p>}
          {record.error_message && <p className="text-ember">{record.error_message}</p>}
          <p><span className="font-semibold">Source report tickers:</span> {symbols(record.source_rows)}</p>
          <p><span className="font-semibold">List before import:</span> {symbols(record.before_members)}</p>
          <p><span className="font-semibold">Recorded list after attempt:</span> {record.after_members === null ? 'Unknown — no confirmed final snapshot' : symbols(record.after_members)}</p>
          {diff && <div aria-label="Recorded membership comparison" className="rounded border border-border p-3">
            <p className="font-semibold">{diff.added.length} Added · {diff.kept.length} Kept · {diff.removed.length} Removed</p>
            <p>Add: {symbols(diff.added)}</p><p>Keep: {symbols(diff.kept)}</p><p>Remove: {symbols(diff.removed)}</p>
            <p className="mt-1 text-xs text-inkMuted">Observed before/after difference; concurrent manual changes may also be included.</p>
          </div>}
          {record.result_rows && <details><summary className="cursor-pointer">Recorded ticker outcomes</summary>
            <ul className="mt-2 space-y-1">{record.result_rows.map(row => <li key={row.id}>
              <span className="font-mono">{row.symbol || 'Empty ticker'}</span> · {row.status} {row.message ? `· ${row.message}` : ''}
              <span className="block break-all text-xs text-inkMuted">Instrument: {row.instrumentId || 'Unresolved / unconfirmed'}</span>
            </li>)}</ul>
          </details>}
          <details><summary className="cursor-pointer">View original CSV</summary>
            <pre className="mt-2 max-h-60 overflow-auto whitespace-pre rounded border border-border bg-canvas2 p-3 text-xs">{new TextDecoder().decode(originalCsvBytes(record.original_base64))}</pre>
          </details>
          {downloadUrl && <a href={downloadUrl} download={record.file_name} className="inline-block rounded border border-border px-3 py-2">Download original CSV</a>}
        </div>}
      </div>
    </section>
  </div>, document.body)
}
